import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';

const ProductsSection = styled.section`
  padding: ${theme.spacing.xxl} 0;
  background-color: ${theme.colors.white};
`;

const Container = styled.div`
  max-width: ${theme.breakpoints.desktop};
  margin: 0 auto;
  padding: 0 ${theme.spacing.xl};
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing.xxl};

  h2 {
    font-size: 2.5rem;
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.md};
  }

  p {
    font-size: 1.1rem;
    color: ${theme.colors.gray.dark};
    max-width: 600px;
    margin: 0 auto;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing.xl};

  @media (max-width: ${theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const ProductCard = styled.div`
  background: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${theme.shadows.medium};
  transition: ${theme.transitions.default};

  &:hover {
    transform: translateY(-5px);
    box-shadow: ${theme.shadows.large};
  }
`;

const ProductImage = styled.div`
  height: 250px;
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.1);
  }
`;

const ProductInfo = styled.div`
  padding: ${theme.spacing.lg};

  h3 {
    font-size: 1.5rem;
    color: ${theme.colors.primary};
    margin-bottom: ${theme.spacing.sm};
  }

  p {
    color: ${theme.colors.gray.dark};
    margin-bottom: ${theme.spacing.md};
  }
`;

const ProductFeatures = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Feature = styled.li`
  color: ${theme.colors.gray.dark};
  margin-bottom: ${theme.spacing.xs};
  display: flex;
  align-items: center;

  &:before {
    content: '✓';
    color: ${theme.colors.secondary};
    margin-right: ${theme.spacing.sm};
  }
`;

const Products: React.FC = () => {
  const products = [
    {
      title: 'Hardwood Lumber',
      image: '/images/hero/hardwood.jpg',
      description: 'Premium quality hardwood for furniture and interior finishing.',
      features: ['Multiple species available', 'Kiln-dried', 'Custom dimensions'],
    },
    {
      title: 'Softwood Lumber',
      image: '/images/hero/softwood.jpg',
      description: 'Versatile softwood perfect for construction and general purposes.',
      features: ['Pressure treated', 'Moisture resistant', 'Cost-effective'],
    },
    {
      title: 'Specialty Plywood',
      image: '/images/hero/specialityply.jpg',
      description: 'High-grade plywood for various applications.',
      features: ['Multiple layers', 'Water-resistant', 'Decorative options'],
    },
    {
      title: 'Live Edge Slabs',
      image: '/images/hero/liveedge.jpg',
      description: 'Unique live edge slabs for custom furniture pieces.',
      features: ['Natural edges', 'Unique patterns', 'Custom sizes'],
    },
    {
      title: 'Decking Materials',
      image: '/images/hero/decking.jpg',
      description: 'Durable decking solutions for outdoor spaces.',
      features: ['Weather-resistant', 'Low maintenance', 'Multiple styles'],
    },
    {
      title: 'Timber Beams',
      image: '/images/hero/beams.jpg',
      description: 'Structural timber beams for construction projects.',
      features: ['High strength', 'Grade stamped', 'Custom lengths'],
    },
  ];

  return (
    <ProductsSection id="products">
      <Container>
        <SectionHeader>
          <h2>Our Premium Products</h2>
          <p>
            Discover our extensive range of high-quality wood products,
            carefully selected and processed to meet your specific needs.
          </p>
        </SectionHeader>
        <ProductGrid>
          {products.map((product, index) => (
            <ProductCard key={index}>
              <ProductImage>
                <img src={product.image} alt={product.title} />
              </ProductImage>
              <ProductInfo>
                <h3>{product.title}</h3>
                <p>{product.description}</p>
                <ProductFeatures>
                  {product.features.map((feature, idx) => (
                    <Feature key={idx}>{feature}</Feature>
                  ))}
                </ProductFeatures>
              </ProductInfo>
            </ProductCard>
          ))}
        </ProductGrid>
      </Container>
    </ProductsSection>
  );
};

export default Products; 